import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, EMPTY, Observable } from 'rxjs';
import { debounceTime, map, startWith, tap } from 'rxjs/operators';
import { OpportunityExpertsService } from '../services/opportunity-experts.service';

export enum DownloadState {
  Initializing = 'init',
  Downloading = 'loading',
  PartiallyDownloaded = 'partial',
  Downloaded = 'loaded',
}

interface ISearch {
  total: number;
  batchId: string;
  sourceIds: string[];
}

type SearchGroups = Record<
  string,
  {
    total: number;
    searches: Record<string, ISearch>;
  }
>;

interface ISearchConfig {
  batchId: string;
  sourceIds: string[];
}

export interface IOpportunitySearchSelectDialogData {
  searches: ISearchConfig[];
  opportunityId: string;
}

@Component({
  selector: 'app-opportunity-search-select-dialog',
  templateUrl: './opportunity-search-select-dialog.component.html',
  styleUrls: ['./opportunity-search-select-dialog.component.scss'],
  standalone: false,
})
export class OpportunitySearchSelectDialogComponent implements OnInit {
  downloadState$: Observable<{
    downloads: Record<string, DownloadState>;
    loading: boolean;
    loaded: boolean;
  }> = EMPTY;
  searches$: Observable<SearchGroups> = EMPTY;

  searchForm = new FormGroup({
    searches: new FormControl<string[]>([]),
    searchTerm: new FormControl<string>(''),
  });

  isDownloadingSearches = true;
  sourceIdMap: Record<string, string[]> = {};

  constructor(
    private dialogRef: MatDialogRef<
      OpportunitySearchSelectDialogComponent,
      ISearchConfig[]
    >,
    @Inject(MAT_DIALOG_DATA)
    private dialogData: IOpportunitySearchSelectDialogData,
    private opportunityExpertsService: OpportunityExpertsService
  ) {}

  ngOnInit() {
    this.downloadState$ =
      this.opportunityExpertsService.downloadedSearches$.pipe(
        startWith<Record<string, DownloadState>>({}),
        map((downloads) => ({
          downloads,
          loading: Object.values(downloads).some((e) =>
            ['init', 'loading'].includes(e)
          ),
          loaded: Object.values(downloads).some((e) =>
            ['partial', 'loaded'].includes(e)
          ),
        }))
      );

    this.searches$ = combineLatest([
      this.opportunityExpertsService.expertTotals$,
      this.searchForm
        .get('searchTerm')
        .valueChanges.pipe(debounceTime(100), startWith<string>('')),
    ]).pipe(
      map(([{ isLoading, batches }, searchTerm]) => {
        this.isDownloadingSearches = isLoading;

        return Object.entries(batches)
          .filter(([, value]) => value.metadata.batchName.includes(searchTerm))
          .reduce<
            Record<
              string,
              {
                total: number;
                metadata: {
                  batchName: string;
                  sourceIds: string[];
                };
              }
            >
          >(
            (prev, [search, value]) => ({
              ...prev,
              [search]: {
                total: Object.values(value.stats || {}).reduce(
                  (p, c) => p + c,
                  0
                ),
                metadata: value.metadata,
              },
            }),
            {}
          );
      }),
      tap((d) => {
        this.sourceIdMap = Object.entries(d).reduce<Record<string, string[]>>(
          (acc, [batchId, stats]) => ({
            ...acc,
            [batchId]: stats.metadata.sourceIds || [],
          }),
          {}
        );
      }),
      map((d) =>
        Object.entries(d).reduce<SearchGroups>((acc, [batchId, stats]) => {
          const groupNames = stats.metadata.batchName.toUpperCase().split('/');
          const groupName =
            groupNames.length > 1 ? groupNames.find(Boolean) : 'Other searches';

          const searchCount = stats.total;

          return {
            ...acc,
            [groupName]: {
              total: (acc[groupName]?.total || 0) + searchCount,
              searches: {
                ...(acc[groupName]?.searches || {}),
                [stats.metadata.batchName]: {
                  total: searchCount,
                  batchId,
                  sourceIds: stats.metadata.sourceIds,
                },
              },
            },
          };
        }, {})
      )
    );

    this.searchForm.setValue({
      searches: this.dialogData.searches.map((s) => s.batchId),
      searchTerm: '',
    });
  }

  submitSearches({ searches }: { searches?: string[] | null }) {
    const mappedSearches = (searches || []).map((s) => ({
      batchId: s,
      sourceIds: this.sourceIdMap[s] || [],
    }));

    this.dialogRef.close(mappedSearches);
  }

  isDownloading(downloadState?: DownloadState) {
    return [DownloadState.Initializing, DownloadState.Downloading].includes(
      downloadState
    );
  }

  getDownloadIcon(downloadState?: DownloadState) {
    if (downloadState === DownloadState.Downloaded) {
      return 'download_done';
    }

    if (this.isDownloading(downloadState)) {
      return 'autorenew';
    }

    return 'download';
  }

  toggleDownload(
    event: Event,
    batch: ISearch,
    batchName: string,
    downloadState?: DownloadState
  ) {
    if (this.isDownloading(downloadState)) {
      return this.cancelDownloads(event);
    }

    return this.downloadSearch(event, batch, batchName);
  }

  downloadSearch(event: Event, batch: ISearch, batchName: string) {
    event.preventDefault();
    event.stopPropagation();

    this.opportunityExpertsService.getSearches({
      opportunityId: this.dialogData.opportunityId,
      batches: [
        { batchId: batch.batchId, batchName, expertSourceIds: batch.sourceIds },
      ],
    });
  }

  cancelDownloads(event: Event) {
    event.preventDefault();
    event.stopPropagation();

    this.opportunityExpertsService.cancelDownloads();
  }

  downloadGroup(
    searches: Record<
      string,
      { total: number; batchId: string; sourceIds: string[] }
    >
  ) {
    const batchIds = Object.entries(searches).map(([k, b]) => ({
      batchId: b.batchId,
      batchName: k,
      expertSourceIds: b.sourceIds,
    }));

    this.opportunityExpertsService.getSearches({
      opportunityId: this.dialogData.opportunityId,
      batches: batchIds,
    });
  }

  toggleSelectAll(searches: SearchGroups) {
    this.searchForm.patchValue({
      searches: Object.values(searches).flatMap((d) =>
        Object.values(d.searches).map((s) => s.batchId)
      ),
    });
  }
}
